<!-- 
Listing of notices.
-->
<template>
  <v-card :class="embedded ? 'v-card--flat' : 'home'">
    <our-table-header
      :listing="listing"
      :search-filters="searchFilters"
      :criteria-mutation="criteriaMutation"
      :has-export="hasExport"
      @update:pageNumber="updatePageNumber"
      @update:criteria="refresh"
      @click:export="exportToExcel"
      @click:refresh="refresh"
    />
    <our-table-busy-indicator :show="listing.loading"/>
    <v-data-table
      :headers="headers" 
      :items="listing.data" 
      :pagination="listing.paging" 
      :total-items="listing.paging.totalItems"
      :rows-per-page-items="rowsPerPageItems" 
      :no-data-text="noDataText"
      must-sort 
      hide-actions
      @click:refresh="refresh" 
      @update:pagination="updatePagination"
    >
      <template slot="items" slot-scope="props">
        <tr @click.stop="showNotice(props.item)">
          <our-nav-col :row="`${props.item.id}`"/>
          <td>{{ props.item.date | compactDateTime }}</td>
          <td>{{ lookups.noticeTypeDescriptions[props.item.type] }}</td>
          <td v-if="!levyCollectionId">{{ props.item.levyCollection }}</td>
          <td v-if="!providerId">{{ props.item.providerName }}</td>
          <td v-if="!forLevyCollectionProvider" class="text-xs-center px-2">
            <v-tooltip bottom>
              <v-btn 
                slot="activator" 
                :to="`/levy-collections/${props.item.levyCollectionId}/providers/${props.item.providerId}/notices`" icon small 
                class="ma-0 primary--text"
              >
                <v-icon small>school</v-icon>
              </v-btn>
              <span>Click to open the levy collection provider</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <our-table-footer :paging="listing.paging" @update:pageNumber="updatePageNumber" @update:rowsPerPage="updateRowsPerPage" />
    <notice-view-dialog v-model="showViewDialog" :notice-id="noticeId" @resend="showResendDialog=true"/>
    <notice-resend-dialog
      v-model="showResendDialog" 
      :levy-collection-id="selectedLevyCollectionId" 
      :provider-id="selectedProviderId" 
      :template-type="noticeTemplateType"
      :notice-id="noticeId"
    />
  </v-card>
</template>

<script>
import NoticeViewDialog from "@/components/notices/NoticeViewDialog.vue";
import NoticeResendDialog from "@/components/notices/NoticeResendDialog.vue";
import tableMixins from "@/mixins/table.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as action from "@/store/notices/action-types";
import * as mutation from "@/store/notices/mutation-types";
import { NOTICES_EXPORT } from "@/store/claim-types";
import { getSearchFilters } from "@/store/notices/helpers";

export default {
  components: {
    NoticeViewDialog,
    NoticeResendDialog
  },
  mixins: [tableMixins, lookupMixins, filterMixins],
  props: {
    embedded: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    },
    providerId: {
      type: Number,
      default: null
    },
    defaultNoDataText: {
      type: String,
      default: "No notices yet."
    }
  },
  data() {
    return {
      action: action.LIST,
      exportAction: action.EXPORT,
      criteriaMutation: mutation.LIST_SET_CRITERIA,
      pagingMutation: mutation.LIST_SET_PAGING,
      showViewDialog: false,
      showResendDialog: false,
      noticeTemplateType: null,
      noticeLevyCollectionId: null,
      noticeProviderId: null,
      noticeId: null
    };
  },
  computed: {
    listing() {
      return this.$store.state.notices.listing;
    },
    loaded() {
      return this.listing.loaded;
    },
    headers() {
      const h = [
        { text: "", value: "Id", class: "navCol", width: 0, sortable: false },
        { text: "Date", value: "Date", sortable: this.canSort },
        { text: "Notice", value: "Type", sortable: this.canSort }
      ];
      if (!this.levyCollectionId) {
        h.push({ text: "Levy Collection", value: "LevyCollection", sortable: this.canSort });
      }
      if (!this.providerId) {
        h.push({ text: "Provider", value: "Provider.Name", sortable: this.canSort });
      }
      if (!this.forLevyCollectionProvider) {
        h.push({ text: "", value: "Id", sortable: false, align: "right", width: 100 });
      }
      return h;
    },
    selectedLevyCollectionId() {
      return this.levyCollectionId || this.noticeLevyCollectionId;
    },
    selectedProviderId() {
      return this.providerId || this.noticeProviderId;
    },
    hasExport() {
      return this.$store.getters.claims.includes(NOTICES_EXPORT);
    },
    forLevyCollectionProvider() {
      return !!this.levyCollectionId && !!this.providerId;
    },
    criteria() {
      return this.listing.criteria;
    },
    searchFilters() {
      return getSearchFilters();
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    providerId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      const filtersHaveChanged =
        this.listing.levyCollectionId != this.levyCollectionId || this.listing.providerId != this.providerId;
      const needsRefreshed = (!this.listing.loaded && !this.listing.loading) || filtersHaveChanged;
      if (filtersHaveChanged) {
        this.$store.commit(mutation.LIST_CLEAR_CRITERIA);
      }
      if (needsRefreshed) {
        this.$store.commit(mutation.LIST_SET_FILTERS, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
        this.populate();
      }
    },
    showNotice(notice) {
      this.noticeTemplateType = notice.type;
      this.noticeLevyCollectionId = notice.levyCollectionId;
      this.noticeProviderId = notice.providerId;
      this.noticeId = notice.id;
      this.showViewDialog = true;
    }
  }
};
</script>
